import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import FormLabel from '@material-ui/core/FormLabel'
import FormControl from '@material-ui/core/FormControl'

import { Form, Formik, getIn } from 'formik'
import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import TextField from '../TextField'

import {
  dateToISO,
  isoDateStrToDateOrNull,
} from '../../helpers'
import theme from '../../theme'

import {
  AttachmentRespType,
  AttachmentType,
  CaseCommonDataType,
  CaseMERespType,
  CaseReportId,
  PartType,
  TreatmentType,
} from '../../models'
import { useStyles } from '../../pages/styled'
import { isEqual } from 'lodash/fp'
import FormGroup from '@material-ui/core/FormGroup'
import { Attachments, AttachmentsChangedEvent } from '../Attachments'
import {
  combineAttachmentsList,
  getAttachmentsFromAttachmentListOrEmpty,
} from '../../models/attachmentListFnc'
import CircleLoadLayout from '../CircleLoadLayout'
import { DestinationPicker } from '../DestinationPicker'
import * as yup from 'yup'
import { DatePicker } from '../DatePicker'
import { Ga4Form } from './Ga4Form'
import ErrorSubmitBack from '../ErrorSubmitBack'
import { BooleanField } from '../BooleanField'
import { RadioButtonsField } from '../RadioButtonsField'
import { objectToLookup } from '../../helpers/miscFunctions'
import { CheckBoxField } from '../CheckBoxField'

export interface HealthProblemFormDataType {
  caseCommon?: CaseCommonDataType | null
  caseME?: CaseMERespType | null
}

const HealthProblemForm = (params: {
  caseReportId: CaseReportId
  healthData: HealthProblemFormDataType | null
  onPageContinue: (healthData: HealthProblemFormDataType) => void
  onPageBack: () => void
  isContinueInProgress: boolean
  isDataLoading?: boolean
  errorMessage?: string
  notifyDataChanged?: (isChanged: boolean) => void
}) => {
  const classes = useStyles(theme)
  const { t } = useTranslation()

  // Funkce 'handleFormSubmit' je volana z formiku a prijma data ve strukture formiku
  const handleFormSubmit = (values: any) => {
    // 1. vezmu values a ziskam z nich podstruktury CaseReportParams
    // ==> OtherDataType
    const healthDataParams: HealthProblemFormDataType =
      transformFormikValueToHealthData(values)
    // 2. pošlu data z komponenty nahoru
    params.onPageContinue(healthDataParams)
  }

  // Funkce 'handleBackButtonClicked' je volana z BackButton
  const handleBackButtonClicked = () => {
    params.onPageBack()
  }

  type FormikData = {
    occurredOn: Date | null
    description: string
    destination: string
    resort: string
    place: string
    assistance: boolean | null
    assistanceDesc: string
    transport: boolean | null
    transportType: string | null
    treatmentTypes: TreatmentType[]
    hospitDateFrom: Date | null
    hospitDateTo: Date | null
    attachments: AttachmentRespType[]

  }
  // Transformuje data z aktualniho formikoveho formulare do struktury modelu
  const transformFormikValueToHealthData = (
    values: FormikData
  ): HealthProblemFormDataType => {
    //Naplnit data z formulare
    return {
      caseCommon: {
        occurredOn: dateToISO(values.occurredOn),
        description: values.description,
        destination: values.destination,
        resort: values.resort,
        place: values.place,
        assistance: values.assistance,
        assistanceDesc: !values.assistance ? values.assistanceDesc : undefined,
      },
      caseME: {
        transport: values.transport ?? undefined,
        transportType: values.transport ? values.transportType ?? undefined : undefined,
        treatmentTypes: values.treatmentTypes,
        hospitDateFrom: dateToISO(values.hospitDateFrom),
        hospitDateTo: dateToISO(values.hospitDateTo),
        //treatmentExpenses: treatmentExpensesRefactored,
        // treatmentExpenses se NEPOŘIZUJÍ NA TOMTO FORMULÁŘI
        attachmentList: combineAttachmentsList(
          params.healthData?.caseME?.attachmentList, // original
          [
            {
              attachmentType: AttachmentType.MEDIC,
              attachments: values.attachments,
            },
          ]
        ),
      },
    }
  }

  // INICIALIZACE FORMULARE
  const formikInitialValues = {
    occurredOn: isoDateStrToDateOrNull(
      params.healthData?.caseCommon?.occurredOn
    ),
    description: params.healthData?.caseCommon?.description ?? '',
    destination: params.healthData?.caseCommon?.destination ?? null,
    resort: params.healthData?.caseCommon?.resort ?? '',
    place: params.healthData?.caseCommon?.place ?? '',
    assistance: params.healthData?.caseCommon?.assistance ?? null,
    assistanceDesc: params.healthData?.caseCommon?.assistanceDesc ?? '',
    transport: params.healthData?.caseME?.transport ?? null,
    transportType: params.healthData?.caseME?.transportType ?? null,
    treatmentTypes: params.healthData?.caseME?.treatmentTypes ?? [],
    hospitDateFrom: isoDateStrToDateOrNull(
      params.healthData?.caseME?.hospitDateFrom
    ),
    hospitDateTo: isoDateStrToDateOrNull(
      params.healthData?.caseME?.hospitDateTo
    ),
    attachments: getAttachmentsFromAttachmentListOrEmpty(
      params.healthData?.caseME?.attachmentList,
      AttachmentType.MEDIC
    ),
  }

  const HealthProblemSchema = () =>
    yup.object().shape({
      occurredOn: yup
        .date()
        .nullable()
        .required(t('dateMessage.required'))
        .min(new Date('1999-12-31'), t('dateMessage.err2'))
        .max(new Date(), t('dateMessage.err2'))
        .typeError(t('dateMessage.err1')),
      destination: yup
        .string()
        .nullable()
        .required(t('thirdPg.destination.required')),
      place: yup.string().required(t('thirdPg.place.required')),
      resort: yup
        .string()
        .required(t('thirdPg.resort.required'))
        .test('len', t('thirdPg.resort.length'), (val: any) => {
          return val?.length <= 50
        }),
      hospitDateFrom: yup
        .mixed()
        .nullable()
        .when('treatmentTypes', {
          is: (treatmentTypes: any) => treatmentTypes?.includes('IP'),
          then: yup
            .date()
            .nullable()
            .required(t('dateMessage.required'))
            .max(new Date(), t('dateMessage.err2'))
            .typeError(t('dateMessage.err1')),
        }),
      hospitDateTo: yup
        .mixed()
        .nullable()
        .when('treatmentTypes', {
          is: (treatmentTypes: TreatmentType[]) => treatmentTypes.includes('IP'),
          then: yup
            .date()
            .nullable()
            .notRequired()
            .typeError(t('dateMessage.err1'))
            .test(
              'hospitDateTo',
              t('dateMessage.err2'),
              (date) => (date == null ? true : date <= new Date())
            )
            .min(
              yup.ref('hospitDateTo') == null
                ? true
                : yup.ref('hospitDateFrom'),
              t('thirdPg.hospitDateTo.err1')
            ),
        }),
      assistance: yup.boolean().nullable().required(t('optionRequired')),
      assistanceDesc: yup
        .mixed()
        .nullable()
        .when('assistance', {
          is: (assistance: boolean) => assistance === false,
          then: yup
            .string()
            .required(t('thirdPg.assistanceDesc.required'))
            .test('len', t('thirdPg.assistanceDesc.length'), (val: any) => {
              return val?.length <= 254
            }),
        }),
      transport: yup.boolean().nullable().required(t('optionRequired')),
      transportType: yup
        .mixed()
        .nullable()
        .when('transport', {
          is: (transport: boolean) => transport,
          then: yup
            .string()
            .nullable()
            .required(t('thirdPg.assistanceDesc.required')),
        }),
      treatmentTypes: yup
        .array()
        .required(t('optionRequired'))
        .min(1, t('optionRequired')),
      // attachments: yup
      //   .mixed()
      //   .nullable()
      //   .when('treatmentTypes', {
      //     is: (treatmentTypes: any) => treatmentTypes?.includes('IP'),
      //     then: yup.array().nullable().min(1, t('uploadFile.err1')),
      //   }),
    })

  const treatmentTypeArray: TreatmentType[] = ['OP', 'IP', 'OTH']
  const formikRef = useRef(null)
  return params.isDataLoading ? (
    <CircleLoadLayout />
  ) : (
    <Ga4Form formName={'HelthProblemForm'} >
      <Formik
        initialValues={formikInitialValues}
        validationSchema={HealthProblemSchema}
        onSubmit={handleFormSubmit}
        enableReinitialize
        innerRef={formikRef}
      >
        {({
          initialValues,
          values,
          errors,
          setFieldValue,
          handleChange,
          touched,
          handleBlur,
        }) => {
          if (params.notifyDataChanged) {
            const isChanged = isEqual(values, initialValues)
            params.notifyDataChanged(isChanged)
          }
          return (
            <Form className={classes.root}>
              <Grid
                item
                sm={12}
                xl={12}
                xs={12}
                container
                direction="row"
                className={classes.gridMainForm}
              >
                <Grid
                  item
                  sm={9}
                  xl={9}
                  xs={12}
                  container
                  direction="row"
                  className={classes.gridWorkArea}
                >
                  <Grid
                    item
                    sm={12}
                    xs={12}
                    xl={12}
                    container
                    className={classes.gridResize}
                  >
                    <Typography className={classes.text} variant="h3">
                      {t('thirdPg.title')}
                    </Typography>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid
                        item
                        xl={12}
                        xs={12}
                        sm={12}
                        className={classes.fieldSize}
                      >
                        <DatePicker
                          label={t('thirdPg.occurredOn.label')}
                          name="occurredOn"
                          helperText={
                            errors.occurredOn && touched.occurredOn
                              ? errors.occurredOn
                              : null
                          }
                          error={Boolean(errors.occurredOn && touched.occurredOn)}
                          required={true}
                          value={values.occurredOn}
                          onChange={(value) => {
                            setFieldValue('occurredOn', value)
                          }}
                          minDate={new Date('2000, 0, 1')}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid
                        item
                        sm={12}
                        xl={12}
                        xs={12}
                        className={classes.fieldSize}
                      >
                        <DestinationPicker
                          name="destination"
                          selectedItemCode={values.destination}
                          onValueChanged={(
                            selectedDestination: string | null
                          ) => {
                            setFieldValue('destination', selectedDestination)
                          }}
                          label={t('thirdPg.destination.label')}
                          helperText={
                            errors.destination && touched.destination
                              ? errors.destination
                              : null
                          }
                          error={Boolean(
                            errors.destination && touched.destination
                          )}
                          required
                          onBlur={handleBlur}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item sm={6} xl={6} xs={6}>
                        <TextField
                          label={t('thirdPg.resort.label')}
                          placeholder={t('thirdPg.resort.hint')}
                          error={!!(errors.resort && touched.resort)}
                          name="resort"
                          value={values.resort}
                          onChange={handleChange}
                          helperText={
                            errors.resort && touched.resort ? errors.resort : null
                          }
                          className={classes.fieldSize}
                          InputProps={{ required: false }}
                          onBlur={handleBlur}
                          required
                        />
                      </Grid>
                      <Grid item sm={6} xl={6} xs={6}>
                        <TextField
                          label={t('thirdPg.place.label')}
                          placeholder={t('thirdPg.place.hint')}
                          name="place"
                          value={values.place}
                          onChange={handleChange}
                          error={!!(errors.place && touched.place)}
                          helperText={
                            errors.place && touched.place ? errors.place : null
                          }
                          className={classes.fieldSize}
                          InputProps={{ required: false }}
                          inputProps={{
                            maxLength: 50,
                          }}
                          onBlur={handleBlur}
                          required
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item xl={12} xs={12} sm={12} className={classes.row}>
                        <BooleanField
                          aria-label="assistance"
                          name="assistance"
                          value={values.assistance}
                          label={t('thirdPg.assistance.label')}
                          onValueChange={(value) => setFieldValue('assistance', value)}
                          required
                          yesText={t('yes')}
                          noText={t('no')}
                          noFirst
                          error={!!(errors.assistance && touched.assistance)}
                          helperText={
                            errors.assistance && touched.assistance ? errors.assistance : null
                          }
                        />
                      </Grid>
                    </Grid>
                    {values.assistance === false && (
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        xl={12}
                        container
                        className={classes.gridItem}
                        spacing={2}
                      >
                        <Grid
                          item
                          sm={12}
                          xl={12}
                          xs={12}
                          className={classes.fieldSize}
                        >
                          <TextField
                            label={t('thirdPg.assistanceDesc.label')}
                            placeholder={t('thirdPg.assistanceDesc.hint')}
                            error={!!(errors.assistanceDesc && touched.assistanceDesc)}
                            name="assistanceDesc"
                            value={values.assistanceDesc}
                            onChange={handleChange}
                            helperText={
                              errors.assistanceDesc && touched.assistanceDesc
                                ? errors.assistanceDesc
                                : null
                            }
                            className={classes.fieldSize}
                            InputProps={{ required: false }}
                            onBlur={handleBlur}
                            required
                          />
                        </Grid>
                      </Grid>
                    )}
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item xl={12} xs={12} sm={12} className={classes.row}>
                        <BooleanField
                          aria-label="transport"
                          name="transport"
                          value={values.transport}
                          label={t('thirdPg.transport.label')}
                          onValueChange={(value) => setFieldValue('transport', value)}
                          required
                          yesText={t('yes')}
                          noText={t('no')}
                          noFirst
                          error={!!(errors.transport && touched.transport)}
                          helperText={
                            errors.transport && touched.transport ? errors.transport : null
                          }
                        />
                      </Grid>
                    </Grid>
                    {values.transport && (
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        xl={12}
                        container
                        className={classes.gridItem}
                        spacing={2}
                      >
                        <Grid
                          item
                          xl={12}
                          xs={12}
                          sm={12}
                          className={classes.row}
                        >
                          <RadioButtonsField
                            aria-label="transportType"
                            name="transportType"
                            label={t('thirdPg.transportType.label')}
                            required
                            value={values.transportType}
                            onChange={handleChange}
                            lookup={objectToLookup(t('thirdPg.transportType.code', { returnObjects: true }))}
                            error={Boolean(errors.transportType && touched.transportType)}
                            helperText={errors.transportType && touched.transportType
                              ? errors.transportType
                              : null}
                          />
                        </Grid>
                      </Grid>
                    )}
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid
                        item
                        sm={12}
                        xl={12}
                        xs={12}
                        className={classes.gridResize}
                      >
                        <TextField
                          aria-label="description"
                          name="description"
                          label={t('thirdPg.description.label')}
                          value={values.description}
                          onChange={handleChange}
                          multiline
                          rows={7}
                          // rowsMin={7}
                          rowsMax={7}
                          placeholder={t('thirdPg.description.hint')}
                          className={classes.textAreaClass}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item xl={12} xs={12} sm={12} className={classes.row}>
                        <FormControl component="fieldset">
                          <FormLabel
                            required
                            error={
                              !!(errors.treatmentTypes && touched.treatmentTypes)
                            }
                          >
                            {t('thirdPg.treatmentTypes.label')}
                          </FormLabel>
                          <FormGroup
                            aria-label="treatmentTypes"
                            className={classes.checkBoxesInRow}
                          >
                            {treatmentTypeArray.map((tt, index) => (
                              <CheckBoxField
                                key={index}
                                name={`treatmentTypes`}
                                label={t(`thirdPg.treatmentTypes.${tt}`)}
                                checked={values.treatmentTypes.includes(tt)}
                                onChange={(e, checked) => {
                                  const _treatmentTypes = values.treatmentTypes
                                  const _index = _treatmentTypes.findIndex((vtt) => vtt === tt)
                                  if (checked) {
                                    if (_index < 0)
                                      _treatmentTypes.push(tt)
                                  }
                                  else
                                    if (_index >= 0)
                                      _treatmentTypes.splice(_index)
                                  setFieldValue(`treatmentTypes`, _treatmentTypes)
                                }}
                              />
                            ))}
                          </FormGroup>

                          {/* 
                          <FormGroup
                            aria-label="treatmentTypes"
                            // onChange={handleChange}
                            className={classes.row}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  name="treatmentTypes"
                                  checked={
                                    values?.treatmentTypes?.includes('IP') ??
                                    false
                                  }
                                  onChange={handleChange}
                                  value="IP"
                                />
                              }
                              label={t('thirdPg.treatmentTypes.type2')}
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  name="treatmentTypes"
                                  checked={
                                    values?.treatmentTypes?.includes('OTH') ??
                                    false
                                  }
                                  onChange={handleChange}
                                  value="OTH"
                                />
                              }
                              label={t('thirdPg.treatmentTypes.type3')}
                            />  
                          </FormGroup>
                          */}
                          {errors.treatmentTypes && touched.treatmentTypes && (
                            <div className={classes.checkError}>
                              {errors.treatmentTypes}
                            </div>
                          )}
                        </FormControl>
                      </Grid>
                    </Grid>
                    {values.treatmentTypes?.includes('IP') && (
                      <React.Fragment>
                        <Grid
                          item
                          sm={12}
                          xs={12}
                          xl={12}
                          container
                          className={classes.gridItem}
                          spacing={2}
                        >
                          <Grid item sm={6} xs={6} xl={6}>
                            <DatePicker
                              label={t('thirdPg.hospitDateFrom.label')}
                              name="hospitDateFrom"
                              error={Boolean(
                                errors.hospitDateFrom && touched.hospitDateFrom
                              )}
                              helperText={
                                errors.hospitDateFrom && touched.hospitDateFrom
                                  ? errors.hospitDateFrom
                                  : null
                              }
                              required={true}
                              minDate={new Date(2000, 0, 1)}
                              maxDate={new Date()}
                              value={values.hospitDateFrom}
                              onChange={(value) => {
                                setFieldValue('hospitDateFrom', value)
                              }}
                            />
                          </Grid>
                          <Grid item sm={6} xs={6} xl={6}>
                            <DatePicker
                              name="hospitDateTo"
                              label={t('thirdPg.hospitDateTo.label')}
                              error={Boolean(
                                errors.hospitDateTo && touched.hospitDateTo
                              )}
                              helperText={
                                errors.hospitDateTo && touched.hospitDateTo
                                  ? errors.hospitDateTo
                                  : null
                              }
                              required={false}
                              minDate={
                                values.hospitDateFrom
                                  ? new Date(values.hospitDateFrom)
                                  : new Date(2000, 0, 1)
                              }
                              maxDate={new Date()}
                              value={values.hospitDateTo}
                              onChange={(value) => {
                                setFieldValue('hospitDateTo', value)
                              }}
                            />
                          </Grid>
                        </Grid>
                        <Attachments
                          name={'attachments'}
                          caseReportId={params.caseReportId}
                          labelText={t('uploadAttachments.CASE.MEDIC')}
                          attachmentType={AttachmentType.MEDIC}
                          part={PartType.CASE}
                          attachments={values.attachments}
                          onAttachmentsChanged={(
                            event: AttachmentsChangedEvent
                          ) => {
                            // aktualizace values formiku
                            setFieldValue('attachments', event.value.attachments)
                          }}
                          onBlur={handleBlur}
                          required={true}
                          error={
                            getIn(errors, 'attachments') &&
                            getIn(touched, 'attachments')
                          }
                          helperText={
                            getIn(errors, 'attachments') &&
                              getIn(touched, 'attachments')
                              ? getIn(errors, 'attachments')
                              : null
                          }
                        />
                      </React.Fragment>
                    )}
                    <ErrorSubmitBack
                      formik={formikRef.current}
                      pending={params.isContinueInProgress}
                      backLinkText={t('thirdPg.backBtn')}
                      onBackLinkClick={handleBackButtonClicked}
                    >
                      {t('continueBtn')}
                    </ErrorSubmitBack>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )
        }}
      </Formik>
    </Ga4Form >
  )
}

export default HealthProblemForm
